import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { BehaviorSubject, forkJoin, Observable, take, throwError } from 'rxjs'
import { catchError, finalize } from 'rxjs/operators'
import { KeyingAppEnvironmentConfig } from '../configuration/keying.app.environment.config'
import { LoadingService } from './loading.service'
import { ErrorService } from './error.service'
import { KeywayService } from '@overtur-monorepo/keying-hierarchy'
import { Chamber, Cylinder, ShearLines } from '../models/dev.test.models'
import { FormatDataService } from './format.data.service'
import { ApiService, Keyway } from '@overtur-monorepo/keying-shared'
import { LoggingService } from './logging.service'
import { TokenizedMessageLoadingService } from './tokenized-message-loading/tokenized-message-loading.service'

@Injectable({
  providedIn: 'root',
})
export class KeyingCylinderService {
  cylinderDataSets = new BehaviorSubject<CylinderDataSets>(null)
  cylinderDataSets$ = this.cylinderDataSets.asObservable()

  cylinders = new BehaviorSubject<Cylinder[]>(null)
  cylinders$ = this.cylinders.asObservable()

  selectedCylinder = new BehaviorSubject<Cylinder>(null)
  selectedCylinder$ = this.selectedCylinder.asObservable()

  onDeleteCylinderSelected = new BehaviorSubject<boolean>(false)
  onDeleteCylinderSelected$ = this.onDeleteCylinderSelected.asObservable()

  saveCylindShearlines = new BehaviorSubject<ShearLines[]>(null)
  saveCylindShearlines$ = this.saveCylindShearlines.asObservable()

  constructor(
    @Inject(HttpClient) private httpClient: HttpClient,
    private keyingAppEnvironmentConfig: KeyingAppEnvironmentConfig,
    private loadingService: LoadingService,
    private errorService: ErrorService,
    private formatDataService: FormatDataService,
    private api: ApiService,
    private keywayService: KeywayService,
    private loggingService: LoggingService,
    private tokenizedMessageLoadingService: TokenizedMessageLoadingService
  ) {}

  private buildUrl(serviceEndpoint: string): string {
    return `${this.keyingAppEnvironmentConfig.baseUrl}/${serviceEndpoint}`
  }

  setSelectedCylinder(cylinder: Cylinder) {
    this.selectedCylinder.next(cylinder)
  }

  setCylinders(cylinders: Cylinder[]) {
    this.cylinders.next(cylinders)
  }

  saveShearlines(isShearlineValidated) {
    this.saveCylindShearlines.next(isShearlineValidated)
  }

  getCylinders(structureId, keySystemId, masterKeyId) {
    const serviceEndpoint = `Structures/${structureId}/keySystems/${keySystemId}/MasterKeys/${masterKeyId}/Cylinders`
    return this.api.get<any>(this.buildUrl(serviceEndpoint))
  }

  getAllCylindersByMasterkeyid(structureId, keySystemId, masterKeyId) {
    const type = 'application/json'
    const headers = new HttpHeaders().set('Content-Type', type)
    const serviceEndpoint = `${this.keyingAppEnvironmentConfig.baseCylinderApiUrl()}/Structures/${structureId}/keySystems/${keySystemId}/Cylinders/MasterKeys/${masterKeyId}`
    return this.httpClient.get<any>(serviceEndpoint, { headers })
  }

  getAllCylindersByMasterkeyidAndType(structureId, keySystemId, masterKeyId, cyclindrType): Observable<any> {
    const token = this.tokenizedMessageLoadingService.startLoading(
      'Loading Cylinders',
      `get-cylinders-${masterKeyId}-${cyclindrType}-${Date.now()}`,
      'medium'
    )

    let type
    switch (cyclindrType) {
      case 'All Cylinders':
        type = 'application/allegion.keying.get.cylinders+json'
        break
      case 'Unverified Cylinders':
        type = 'application/allegion.keying.get.unverified.cylinders+json'
        break
      case 'CrossKeyed Cylinders':
        type = 'application/allegion.keying.get.xkey.cylinders+json'
        break
    }

    const headers = new HttpHeaders().set('Content-Type', type)
    const serviceEndpoint = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/Structures/${structureId}/keySystems/${keySystemId}/MasterKeys/${masterKeyId}/cylinders`

    return this.httpClient.get<any>(serviceEndpoint, { headers }).pipe(
      catchError((error) => {
        this.errorService.throw(error)
        return throwError(() => error)
      }),
      finalize(() => this.tokenizedMessageLoadingService.stopLoading(token))
    )
  }

  patchCylinder(structureId, keySystemId, masterKeyId, cylinderId, body): Observable<Cylinder> {
    const type = 'application/allegion.keying.update.cylinder.keyway+json'
    const headers = new HttpHeaders().set('Content-Type', type)
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/Structures/${structureId}/keySystems/${keySystemId}/MasterKeys/${masterKeyId}/Cylinders/${cylinderId}`
    return this.httpClient.patch<Cylinder>(url, body, { headers })
  }

  postCylinderShearLines(structureId, keySystemId, masterKeyId, masterKeyCylinderId, save = false, body) {
    let type = 'application/allegion.keying.validateshearlines+json'

    let headers = new HttpHeaders().set('Content-Type', type)
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/Structures/${structureId}/keySystems/${keySystemId}/MasterKeys/${masterKeyId}/Cylinders/${masterKeyCylinderId}/ShearLines`

    return this.httpClient.post<any>(url, body, { headers })
  }

  sortShearLines(structureId, keySystemId, masterKeyId, masterKeyCylinderId, body) {
    let type = 'application/allegion.keying.sortshearlines+json'

    let headers = new HttpHeaders().set('Content-Type', type)
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/Structures/${structureId}/keySystems/${keySystemId}/MasterKeys/${masterKeyId}/Cylinders/${masterKeyCylinderId}/ShearLines`

    return this.httpClient.post<any>(url, body, { headers })
  }

  generateShearLines(structureId, keySystemId, masterKeyId, cylinderId) {
    const type = 'application/allegion.keying.generate.shearlines+json'

    const headers = new HttpHeaders().set('Content-Type', type)
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}/masterKeys/${masterKeyId}/cylinders/${cylinderId}/shearlines`

    return this.httpClient.get<any>(url, { headers })
  }

  resetShearLines(structureId, keySystemId, masterKeyId, cylinderId) {
    const type = 'application/allegion.keying.reset.shearlines+json'

    const headers = new HttpHeaders().set('Content-Type', type)
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}/masterKeys/${masterKeyId}/cylinders/${cylinderId}/shearlines`

    return this.httpClient.get<any>(url, { headers })
  }

  putCylinderShearLines(structureId, keySystemId, masterKeyId, cylinderId, save = false, body, auditTrailRequest) {
    let type

    const req = {
      chambers: body.chambers,
      id: body.id,
      length: body.length,
      masterKeyCylinderId: body.masterKeyCylinderId,
      shearLineDepths: body.shearLineDepths,
      auditTrailRequest: auditTrailRequest,
    }

    switch (save) {
      case true:
        type = 'application/json'
        break
      default:
        type = 'application/allegion.keying.validateshearlines+json'
        break
    }

    const headers = new HttpHeaders().set('Content-Type', type)

    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}/masterKeys/${masterKeyId}/cylinders/${cylinderId}/shearlines`

    if (req.shearLineDepths.length > 0) {
      return this.httpClient.put<any>(url, req, { headers })
    } else {
      return this.httpClient.put<any>(url, { headers })
    }
  }

  addOperators(structureId, keySystemId, masterKeyId, cylinderId, body) {
    const contentType = 'application/allegion.keying.add.operators+json'

    const headers = new HttpHeaders().set('Content-Type', contentType)
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}/masterKeys/${masterKeyId}/cylinders/${cylinderId}`

    return this.httpClient.put<any>(url, body, { headers })
  }

  addXKeyOperators(structureId, keySystemId, masterKeyId, cylinderId, body) {
    const contentType = 'application/allegion.keying.add.cross.key.operators+json'

    const headers = new HttpHeaders().set('Content-Type', contentType)
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}/masterKeys/${masterKeyId}/cylinders/${cylinderId}`

    return this.httpClient.put<any>(url, body, { headers })
  }

  addCylinder(structureId, keySystemId, masterKeyId, cylinderId, body) {
    const contentType = 'application/allegion.keying.add.cylinder+json'

    const headers = new HttpHeaders().set('Content-Type', contentType)
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}/masterKeys/${masterKeyId}/cylinders/${cylinderId}`

    return this.httpClient.post<any>(url, body, { headers })
  }

  verifyCylinder(structureId, keySystemId, masterKeyId, cylinderId) {
    const contentType = 'application/allegion.keying.verify.cylinder+json'

    const headers = new HttpHeaders().set('Content-Type', contentType)

    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}/masterKeys/${masterKeyId}/cylinders/${cylinderId}`

    return this.httpClient.patch<any>(url, null, { headers })
  }

  validateCylinderShearLines(structureId, keySystemId, masterKeyId, cylinderId) {
    let body

    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/Structures/${structureId}/keySystems/${keySystemId}/MasterKeys/${masterKeyId}/Cylinders/${cylinderId}/ShearLines`
    return this.httpClient.post<any[]>(url, body)
  }

  validateCylinder(structureId, keySystemId, masterKeyId, cylinderId): Observable<Cylinder> {
    const type = 'application/allegion.keying.validate.cylinder+json'

    const headers = new HttpHeaders().set('Content-Type', type).set('Accept', 'application/json')

    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}/masterKeys/${masterKeyId}/cylinders/${cylinderId}`
    return this.httpClient.patch<Cylinder>(url, null, { headers })
  }

  cylinderHasOpenOrder(structureId, keySystemId, masterKeyId, cylinderId) {
    const contentType = 'application/allegion.keying.check.cylinder.has.open.order+json'
    const headers = new HttpHeaders().set('Content-Type', contentType)
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}/masterKeys/${masterKeyId}/cylinders/${cylinderId}`

    return this.httpClient.get<any>(url, { headers })
  }

  deleteCylinder(
    structureId,
    keySystemId,
    masterKeyId,
    cylinderId,
    auditTrailRequest: any = undefined
  ): Observable<Cylinder> {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}/masterKeys/${masterKeyId}/cylinders/${cylinderId}`
    return this.httpClient.delete<Cylinder>(url, { body: { auditTrailRequest: auditTrailRequest || {} } })
  }

  createDeactCylinders(structureId, keySystemId) {
    const contentType = 'application/allegion.keying.add.deact.cylinders+json'
    const headers = new HttpHeaders().set('Content-Type', contentType)
    const url = `${this.keyingAppEnvironmentConfig.baseCylinderApiUrl()}/Structures/${structureId}/KeySystems/${keySystemId}/Cylinders`

    return this.httpClient.post<any>(url, null, { headers })
  }

  getCylinder(structureId, keySystemId, masterKeyId, cylinderId): Observable<Cylinder> {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/Structures/${structureId}/keySystems/${keySystemId}/MasterKeys/${masterKeyId}/Cylinders/${cylinderId}`
    return this.httpClient.get<Cylinder>(url)
  }

  getCylinderKeyways(structureId, keySystemId, cylinderId): Observable<Keyway[]> {
    const type = 'application/allegion.keying.cylinder.keyways+json'

    const headers = new HttpHeaders().set('Accept', type)
    const url = `${this.keyingAppEnvironmentConfig.baseCylinderApiUrl()}/Structures/${structureId}/keySystems/${keySystemId}/Cylinders/${cylinderId}`
    return this.httpClient.get<Keyway[]>(url, { headers })
  }

  deleteOperators(structureId, keySystemId, cylinderId, body) {
    const type = 'application/allegion.keying.delete.operators+json'

    const headers = new HttpHeaders().set('Content-Type', type)
    const url = `${this.keyingAppEnvironmentConfig.baseCylinderApiUrl()}/Structures/${structureId}/keySystems/${keySystemId}/Cylinders/${cylinderId}`

    return this.httpClient.put<any>(url, body, { headers })
  }

  loadCylinders(structure, keySystemId, masterKeyId) {
    this.loadingService.set({ isLoading: true, message: 'Loading Cylinders' })
    forkJoin([this.getCylinders(structure.id, keySystemId, masterKeyId)])
      .pipe(take(1))
      .subscribe(
        (resData) => {
          const newData = new CylinderDataSets()

          this.loggingService.log('Cylinder Data Loaded:', newData)

          newData.cylinders = this.formatDataService.formatCylinders(resData[0])

          newData.cylinders.map((cyl) => {
            if (cyl.keywayId && !cyl.keywayName) {
              this.keywayService
                .getKeyways(structure.keywayFamilyId, cyl.keywayId)
                .pipe(take(1))
                .subscribe((keyway) => {
                  cyl.keywayName = keyway.name
                })
            }

            this.setCylinders(newData.cylinders)
          })

          this.cylinderDataSets.next(newData)

          this.loadingService.set({ isLoading: false, message: '' })
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
        }
      )
  }

  audit(cylinder: Cylinder) {
    let auditedCylinder = cylinder
    let rules = new Rules()
    let numEmpty = 0

    cylinder.chambers.forEach((chamber: Chamber, index) => {
      if (chamber.isEmpty) {
        numEmpty = numEmpty + 1
        cylinder.shearLines.shearLineDepths.forEach((row) => {
          row[index] = 'E'
        })
      } else if (!chamber.isEmpty) {
        cylinder.shearLines.shearLineDepths.forEach((row) => {
          if (row[index] === 'E') {
            row[index] = Math.floor(Math.random() * (5 - 0) + 1)
          }
        })
      }
    })

    if (numEmpty >= rules.numCanEmpty) {
      cylinder.chambers.forEach((chamber: Chamber, index) => {
        if (!chamber.isEmpty) {
          chamber.canEmpty = false
        }
      })
    } else {
      cylinder.chambers.forEach((chamber: Chamber, index) => {
        if (!chamber.isEmpty && index !== 0) {
          chamber.canEmpty = true
        }
      })
    }

    this.loggingService.log('Audited!', auditedCylinder)
    return auditedCylinder
  }

  addRow(cylinder: Cylinder) {
    let i = cylinder.formattedShearLines.length
    let row = []
    while (i > 0) {
      row.push('X')
      i--
    }
    const newMatrix = [row].concat(cylinder.formattedShearLines.shearLineDepths)
    cylinder.formattedShearLines.shearLineDepths = newMatrix
    // cylinder = this.audit(cylinder)

    return cylinder
  }
}

export class CylinderDataSets {
  cylinders
}

export class Rules {
  numCanEmpty = 2
}
